// Tipos de documento
export type DocumentType = 'cpf' | 'cnpj';

// Tipos de conta bancária
export type BankAccountType = 'corrente' | 'poupanca';

// Interface base para endereço
export interface Address {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

// Interface base para informações bancárias
export interface BankInfo {
  bankName?: string;
  agency?: string;
  account?: string;
  accountType?: BankAccountType;
}

// Interface base para contato
export interface ContactInfo {
  email?: string;
  phone?: string;
}

// Interface completa para clientes e fornecedores
export interface Contact {
  id: string;
  userId: string;
  name: string;
  documentType?: DocumentType | null;
  documentNumber?: string | null;
  registrationCode?: string | null;
  email?: string;
  phone?: string;
  address?: Address;
  bankInfo?: BankInfo;
  paymentTerms?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Cliente
export interface Client extends Contact {
  // Campos específicos de clientes (se necessário no futuro)
}

// Fornecedor
export interface Supplier extends Contact {
  // Campos específicos de fornecedores (se necessário no futuro)
}

// Tipos para formulários (flattened para facilitar uso com react-hook-form)
export interface ClientFormData {
  name: string;
  documentType?: DocumentType | null;
  documentNumber?: string;
  registrationCode?: string | null;
  email?: string;
  phone?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: BankAccountType;
  paymentTerms?: string;
  notes?: string;
  isActive?: boolean;
}

export interface SupplierFormData {
  name: string;
  documentType: DocumentType;
  documentNumber?: string | null;
  email?: string;
  phone?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: BankAccountType;
  paymentTerms?: string;
  notes?: string;
  isActive?: boolean;
}

// Funções de transformação
export function formDataToClient(formData: ClientFormData, userId: string): Omit<Client, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    userId,
    name: formData.name,
    documentType: formData.documentType || null,
    documentNumber: formData.documentNumber || null,
    registrationCode: formData.registrationCode || null,
    email: formData.email,
    phone: formData.phone,
    address: {
      street: formData.addressStreet,
      number: formData.addressNumber,
      complement: formData.addressComplement,
      neighborhood: formData.addressNeighborhood,
      city: formData.addressCity,
      state: formData.addressState,
      zipCode: formData.addressZipCode,
    },
    bankInfo: {
      bankName: formData.bankName,
      agency: formData.bankAgency,
      account: formData.bankAccount,
      accountType: formData.bankAccountType,
    },
    paymentTerms: formData.paymentTerms,
    notes: formData.notes,
    isActive: formData.isActive ?? true,
  };
}

export function clientToFormData(client: Client): ClientFormData {
  return {
    name: client.name,
    documentType: client.documentType,
    documentNumber: client.documentNumber,
    registrationCode: client.registrationCode || null,
    email: client.email || "",
    phone: client.phone || "",
    addressStreet: client.address?.street || "",
    addressNumber: client.address?.number || "",
    addressComplement: client.address?.complement || "",
    addressNeighborhood: client.address?.neighborhood || "",
    addressCity: client.address?.city || "",
    addressState: client.address?.state || "",
    addressZipCode: client.address?.zipCode || "",
    bankName: client.bankInfo?.bankName || "",
    bankAgency: client.bankInfo?.agency || "",
    bankAccount: client.bankInfo?.account || "",
    bankAccountType: client.bankInfo?.accountType || "corrente",
    paymentTerms: client.paymentTerms || "",
    notes: client.notes || "",
    isActive: client.isActive,
  };
}

export function formDataToSupplier(formData: SupplierFormData, userId: string): Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    userId,
    name: formData.name,
    documentType: formData.documentType,
    documentNumber: formData.documentNumber,
    email: formData.email,
    phone: formData.phone,
    address: {
      street: formData.addressStreet,
      number: formData.addressNumber,
      complement: formData.addressComplement,
      neighborhood: formData.addressNeighborhood,
      city: formData.addressCity,
      state: formData.addressState,
      zipCode: formData.addressZipCode,
    },
    bankInfo: {
      bankName: formData.bankName,
      agency: formData.bankAgency,
      account: formData.bankAccount,
      accountType: formData.bankAccountType,
    },
    paymentTerms: formData.paymentTerms,
    notes: formData.notes,
    isActive: formData.isActive ?? true,
  };
}

export function supplierToFormData(supplier: Supplier): SupplierFormData {
  return {
    name: supplier.name,
    documentType: supplier.documentType,
    documentNumber: supplier.documentNumber,
    email: supplier.email || "",
    phone: supplier.phone || "",
    addressStreet: supplier.address?.street || "",
    addressNumber: supplier.address?.number || "",
    addressComplement: supplier.address?.complement || "",
    addressNeighborhood: supplier.address?.neighborhood || "",
    addressCity: supplier.address?.city || "",
    addressState: supplier.address?.state || "",
    addressZipCode: supplier.address?.zipCode || "",
    bankName: supplier.bankInfo?.bankName || "",
    bankAgency: supplier.bankInfo?.agency || "",
    bankAccount: supplier.bankInfo?.account || "",
    bankAccountType: supplier.bankInfo?.accountType || "corrente",
    paymentTerms: supplier.paymentTerms || "",
    notes: supplier.notes || "",
    isActive: supplier.isActive,
  };
}